import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import { DEV_URL, rget, rpost } from "../../utils/requests";
import FileUploader from "components/UnifiedFileUploader";

interface AddHuBulkProps {
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const AddHuBulk = ({ show, onClose, onSaved }: AddHuBulkProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");

  const handleFileChange = (files: File[]) => {
    const file = files[0] || null;
    setSelectedFile(file);
    setMessage("");
  };

  const handleRejectedFiles = () => {
    setSelectedFile(null);
    setMessage("Please upload a .xlsx, .xls, or .csv file.");
    setMessageType("error");
  };

  const resetForm = () => {
    setSelectedFile(null);
    setMessage("");
    setMessageType("");
  };

  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await rget(
        `${DEV_URL}/api/HistoricalUsage/download-blob`
      );
      const link = document.createElement("a");
      link.href = response?.data?.downloadUrl;
      link.download = "HU_Bulk_Template.xlsx";
      link.click();
    } catch (error) {
      console.error("Download error:", error);
      setMessage("Error downloading template.");
      setMessageType("error");
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage("Excel file is required.");
      setMessageType("error");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("companyId", "1"); 
    try {
      setLoading(true);
      setMessage("");

      const response = await rpost(
        `${DEV_URL}/api/HistoricalUsage/hu-requests/bulk-upload`,
        formData
      );

      if (response?.data?.message) {
        setMessage(response.data.message);
        setMessageType(response.status >= 200 && response.status < 300 ? "success" : "error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setMessage("Error uploading file. Please check the rows that failed and try again.");
      setMessageType("error");
    } finally {
      setLoading(false);
      resetForm();
      onSaved?.();
      onClose();
    }
  };

  return (
    <ConfirmationModal
      dialogClassName="modal-md"
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      title="Upload HU"
      confirmButtonLabel={loading ? "Uploading..." : "Add"}
      isLoading={loading}
      onConfirm={handleUpload}
    >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Form.Label className="fw-semibold mb-0">
            Upload Excel file
          </Form.Label>

          <Button
            type="button"
            variant="link"
            onClick={handleDownloadTemplate}
            style={{
              border: "none",
              background: "transparent",
              color: "#6c757d",
              fontSize: "12px",
              fontWeight: 600,
              padding: 0,
              boxShadow: "none",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <i className="uil uil-download-alt"></i>
            Template
          </Button>
        </div>

        <FileUploader
          value={selectedFile ? [selectedFile] : []}
          name="historicalUsageBulkFile"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
          onRejected={handleRejectedFiles}
          multiple={false}
          disabled={loading}
          customPlaceholder={<h5>Drop a file here or click to upload.</h5>}
        />

        <Form.Text className="text-muted">
          Upload .xlsx, .xls, or .csv file
        </Form.Text>

        {message && (
          <div
            className={`alert alert-${
              messageType === "error" ? "danger" : "success"
            } mt-2`}
          >
            {message}
          </div>
        )}
    </ConfirmationModal>
  );
};

export default AddHuBulk;
